import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ConfigProvider, theme as antdTheme } from 'antd'
import AntdConfig from 'src/cms/utils/antdConfig'
import './darkMode.css'

export type ThemeMode = 'light' | 'dark'

export const THEME_MODE_STORAGE_KEY = 'themeMode'

// localStorage so the choice survives closing the browser. Logout runs
// localStorage.clear(), so the provider re-writes the key on pagehide too.
const readStoredMode = (): ThemeMode => {
  try {
    return localStorage.getItem(THEME_MODE_STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const writeStoredMode = (mode: ThemeMode) => {
  try {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
  } catch {
    // localStorage unavailable (private mode etc.) -- keep in-memory only
  }
}

type ThemeModeContextValue = {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  toggleMode: () => void
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
})

export const useThemeMode = () => useContext(ThemeModeContext)

// AntdConfig hardcodes several light-only colours (white header/table/button
// backgrounds, near-black select text). These replace them in dark mode so the
// darkAlgorithm's derived tokens show through.
// Keep in sync with the --dm-* variables in darkMode.css.
const DARK_BG = '#000000'
const DARK_SURFACE = '#121212'
const DARK_ELEVATED = '#1c1c1c'
const DARK_BORDER = '#2e2e2e'
const DARK_TEXT = '#ffffff'

const darkTokens = {
  colorBgBase: DARK_BG,
  colorTextBase: DARK_TEXT,
  colorBgLayout: DARK_BG,
  colorBgContainer: DARK_SURFACE,
  colorBgElevated: DARK_ELEVATED,
  colorBorder: '#3a3a3a',
  colorBorderSecondary: DARK_BORDER,
  colorText: DARK_TEXT,
  colorTextHeading: DARK_TEXT,
  colorTextLabel: DARK_TEXT,
  colorTextSecondary: 'rgba(255, 255, 255, 0.75)',
  colorTextTertiary: 'rgba(255, 255, 255, 0.6)',
  colorTextPlaceholder: 'rgba(255, 255, 255, 0.45)',
  colorTextDisabled: 'rgba(255, 255, 255, 0.4)',
  colorIcon: 'rgba(255, 255, 255, 0.75)',
}

const darkComponentOverrides = {
  Layout: { headerBg: DARK_SURFACE, bodyBg: 'transparent', siderBg: DARK_SURFACE },
  Typography: { colorTextHeading: DARK_TEXT, colorText: DARK_TEXT },
  Table: {
    colorBgContainer: DARK_SURFACE,
    colorBorderSecondary: DARK_BORDER,
    colorText: DARK_TEXT,
    rowHoverBg: DARK_ELEVATED,
  },
  Menu: {
    itemColor: DARK_TEXT,
    itemBg: 'transparent',
    subMenuItemBg: 'transparent',
    itemHoverBg: DARK_ELEVATED,
    itemSelectedBg: DARK_ELEVATED,
    itemSelectedColor: '#8fb8ff',
  },
  Button: { defaultBg: 'transparent', defaultColor: DARK_TEXT, defaultBorderColor: '#5a5a5a' },
  Select: { colorText: DARK_TEXT, colorPrimary: '#8fb8ff', colorBgContainerDisabled: '#1a1a1a' },
  Input: { colorIcon: DARK_TEXT, colorBgContainerDisabled: '#1a1a1a' },
  InputNumber: { addonBg: DARK_SURFACE, colorPrimary: '#434343', colorBgContainerDisabled: '#1a1a1a' },
  DatePicker: { colorBgContainerDisabled: '#1a1a1a' },
  Radio: { colorBgContainerDisabled: '#1a1a1a' },
  Checkbox: { colorBgContainerDisabled: '#1a1a1a' },
  Collapse: { colorText: DARK_TEXT, colorTextHeading: DARK_TEXT, headerBg: DARK_SURFACE },
  Result: { colorTextDescription: 'rgba(255, 255, 255, 0.75)' },
  Form: { labelColor: DARK_TEXT },
  Card: { colorBgContainer: DARK_SURFACE },
  Modal: { contentBg: DARK_SURFACE, headerBg: DARK_SURFACE, footerBg: DARK_SURFACE },
  Drawer: { colorBgElevated: DARK_SURFACE },
  Tabs: { itemColor: DARK_TEXT },
  Descriptions: { labelBg: DARK_ELEVATED, colorText: DARK_TEXT, titleColor: DARK_TEXT },
}

export const ThemeModeProvider: React.FC = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode)
    writeStoredMode(mode)

    // Logout clears localStorage and then reloads/redirects; restore the key
    // as the page goes away so the next load still has it.
    const restore = () => writeStoredMode(mode)
    window.addEventListener('pagehide', restore)
    return () => window.removeEventListener('pagehide', restore)
  }, [mode])

  const antdThemeConfig = useMemo(() => {
    if (mode === 'light') return AntdConfig
    const components: any = { ...AntdConfig.components }
    Object.entries(darkComponentOverrides).forEach(([name, overrides]) => {
      components[name] = { ...components[name], ...overrides }
    })
    return {
      ...AntdConfig,
      algorithm: antdTheme.darkAlgorithm,
      token: { ...AntdConfig.token, ...darkTokens },
      components,
    }
  }, [mode])

  // Static antd APIs (Modal.confirm, message, notification) render outside this
  // tree and ignore the ConfigProvider above, so they stayed light. Route them
  // through the theme too -- dark only, so light mode renders exactly as before.
  useEffect(() => {
    ConfigProvider.config({
      holderRender: (node) =>
        mode === 'dark' ? (
          <ConfigProvider theme={antdThemeConfig as any}>{node}</ConfigProvider>
        ) : (
          node
        ),
    })
  }, [mode, antdThemeConfig])

  const value = useMemo(
    () => ({
      mode,
      setMode: setModeState,
      toggleMode: () => setModeState((m) => (m === 'dark' ? 'light' : 'dark')),
    }),
    [mode]
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ConfigProvider theme={antdThemeConfig as any}>{children}</ConfigProvider>
    </ThemeModeContext.Provider>
  )
}
